import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useCallback,
  useRef,
  useState,
} from 'react';
import { DT } from '../theme/designTokens';
import { useAuth } from './AuthContext';
import { useFriends } from './FriendContext';
import { useDmPresentationSuppressed } from '../hooks/useDmPresentationSuppressed';

// ─── Presentation state (Docked Chat Redesign — desktop slice) ───────────────
//
// ChatDockContext owns ONLY presentation concerns for the desktop DM dock:
// which conversations are open, expanded/minimized state, focus order,
// desktop window limits and overflow, per-conversation drafts, and route
// suppression. FriendContext remains the sole data/transport authority —
// no message arrays are duplicated here and no additional FriendChannel
// subscription exists. All state is in-memory only (no browser-storage
// persistence in this slice) and is destroyed on logout/account switch.

/** Standard desktop shows at most two expanded windows (design target). */
const MAX_EXPANDED_DESKTOP = 2;
/** Ultrawide displays may show a third window — verified to fit cleanly. */
const MAX_EXPANDED_ULTRAWIDE = 3;
/** Viewport width from which a third expanded window is allowed. */
export const ULTRAWIDE_MIN_WIDTH = 1920;
/** At most this many minimized tabs render individually; the rest collapse
 *  into the deterministic overflow control. */
export const MAX_VISIBLE_TABS = 3;

export type DockEventKind = 'opened' | 'restored' | 'minimized' | 'closed';

interface DockEvent {
  kind: DockEventKind;
  id:   number;
  /** Monotonic sequence so identical consecutive events still re-fire effects. */
  seq:  number;
}

interface DockState {
  /** Conversation ids currently in the dock (expanded OR minimized), in the
   *  order they were opened. */
  open: number[];
  /** Expanded ids in focus order: least-recently-focused first, the LAST
   *  entry is the focused window. */
  focusOrder: number[];
  /** Minimized ids, most recently minimized FIRST (tab render order). */
  minimizeOrder: number[];
  /** Sidebar Chat module expanded/collapsed. */
  moduleExpanded: boolean;
  /** Last user-initiated transition — drives focus restoration in the layer.
   *  Auto-minimizes from limit enforcement deliberately do NOT set this. */
  lastEvent: DockEvent | null;
}

const initialDockState: DockState = {
  open:           [],
  focusOrder:     [],
  minimizeOrder:  [],
  // The chats panel is a click-to-open flyout (chess.com pattern) — closed
  // by default; only the compact CHAT button shows until opened.
  moduleExpanded: false,
  lastEvent:      null,
};

type DockAction =
  | { type: 'OPEN';            id: number; maxExpanded: number }
  | { type: 'FOCUS';           id: number }
  | { type: 'MINIMIZE';        id: number }
  | { type: 'RESTORE';         id: number; maxExpanded: number }
  | { type: 'CLOSE';           id: number }
  | { type: 'ENFORCE_LIMIT';   maxExpanded: number }
  | { type: 'SET_MODULE_EXPANDED'; expanded: boolean }
  | { type: 'RESET' };

/** Minimize least-recently-focused expanded windows until the limit holds.
 *  Limit enforcement is NOT a user gesture — lastEvent is left untouched so
 *  focus never jumps because of an auto-minimize. */
function enforceLimit(state: DockState, maxExpanded: number): DockState {
  if (state.focusOrder.length <= maxExpanded) return state;
  const focusOrder = [...state.focusOrder];
  const minimizeOrder = [...state.minimizeOrder];
  while (focusOrder.length > maxExpanded) {
    const lru = focusOrder.shift()!;
    minimizeOrder.unshift(lru);
  }
  return { ...state, focusOrder, minimizeOrder };
}

function nextEvent(state: DockState, kind: DockEventKind, id: number): DockEvent {
  return { kind, id, seq: (state.lastEvent?.seq ?? 0) + 1 };
}

function dockReducer(state: DockState, action: DockAction): DockState {
  switch (action.type) {
    case 'OPEN': {
      const { id, maxExpanded } = action;
      const alreadyOpen = state.open.includes(id);
      const open = alreadyOpen ? state.open : [...state.open, id];
      const minimizeOrder = state.minimizeOrder.filter(x => x !== id);
      const focusOrder = [...state.focusOrder.filter(x => x !== id), id];
      const wasExpanded = state.focusOrder.includes(id);
      const next = enforceLimit({ ...state, open, focusOrder, minimizeOrder }, maxExpanded);
      return {
        ...next,
        lastEvent: nextEvent(state, alreadyOpen ? (wasExpanded ? 'opened' : 'restored') : 'opened', id),
      };
    }
    case 'FOCUS': {
      const { id } = action;
      if (!state.focusOrder.includes(id)) return state;
      if (state.focusOrder[state.focusOrder.length - 1] === id) return state;
      return { ...state, focusOrder: [...state.focusOrder.filter(x => x !== id), id] };
    }
    case 'MINIMIZE': {
      const { id } = action;
      if (!state.focusOrder.includes(id)) return state;
      return {
        ...state,
        focusOrder:    state.focusOrder.filter(x => x !== id),
        minimizeOrder: [id, ...state.minimizeOrder.filter(x => x !== id)],
        lastEvent:     nextEvent(state, 'minimized', id),
      };
    }
    case 'RESTORE': {
      const { id, maxExpanded } = action;
      if (!state.minimizeOrder.includes(id)) return state;
      const next = enforceLimit(
        {
          ...state,
          minimizeOrder: state.minimizeOrder.filter(x => x !== id),
          focusOrder:    [...state.focusOrder.filter(x => x !== id), id],
        },
        maxExpanded,
      );
      return { ...next, lastEvent: nextEvent(state, 'restored', id) };
    }
    case 'CLOSE': {
      const { id } = action;
      if (!state.open.includes(id)) return state;
      return {
        ...state,
        open:          state.open.filter(x => x !== id),
        focusOrder:    state.focusOrder.filter(x => x !== id),
        minimizeOrder: state.minimizeOrder.filter(x => x !== id),
        lastEvent:     nextEvent(state, 'closed', id),
      };
    }
    case 'ENFORCE_LIMIT':
      return enforceLimit(state, action.maxExpanded);
    case 'SET_MODULE_EXPANDED':
      return { ...state, moduleExpanded: action.expanded };
    case 'RESET':
      return initialDockState;
    default:
      return state;
  }
}

// ─── Context value ───────────────────────────────────────────────────────────

export interface ChatDockValue {
  /** Every conversation in the dock (expanded or minimized), opening order. */
  openIds:      number[];
  /** Expanded ids, least-recently-focused first. */
  expandedIds:  number[];
  /** Minimized ids, most recently minimized first. */
  minimizedIds: number[];
  /** The focused expanded window, or null. */
  focusedId:    number | null;
  moduleExpanded: boolean;
  lastEvent:    DockEvent | null;
  /** Desktop dock active (viewport ≥ desktop breakpoint). Below it the
   *  existing mobile Messages experience remains authoritative. */
  isDesktopDock: boolean;
  /** Current expanded-window limit (2 standard desktop, 3 ultrawide). */
  maxExpanded:  number;
  /** Route suppression (game/spectate/tutorial) — presentation only. */
  suppressed:   boolean;
  /** Document (browser tab) visibility — read-eligibility input. */
  documentVisible: boolean;
  openConversation:     (friendId: number) => void;
  focusConversation:    (friendId: number) => void;
  minimizeConversation: (friendId: number) => void;
  restoreConversation:  (friendId: number) => void;
  closeConversation:    (friendId: number) => void;
  setModuleExpanded:    (expanded: boolean) => void;
  /** Unsent composer draft (HTML) per open conversation. Ref-backed: writing
   *  never re-renders the tree; drafts die with close/logout. */
  getDraft: (friendId: number) => string;
  setDraft: (friendId: number, html: string) => void;
  /** Sidebar module header registers itself as the close-focus target. */
  registerModuleHeader: (el: HTMLElement | null) => void;
  getModuleHeader:      () => HTMLElement | null;
}

const noop = () => {};

const ChatDockContext = createContext<ChatDockValue>({
  openIds:          [],
  expandedIds:      [],
  minimizedIds:     [],
  focusedId:        null,
  moduleExpanded:   false,
  lastEvent:        null,
  isDesktopDock:    false,
  maxExpanded:      MAX_EXPANDED_DESKTOP,
  suppressed:       false,
  documentVisible:  true,
  openConversation:     noop,
  focusConversation:    noop,
  minimizeConversation: noop,
  restoreConversation:  noop,
  closeConversation:    noop,
  setModuleExpanded:    noop,
  getDraft: () => '',
  setDraft: noop,
  registerModuleHeader: noop,
  getModuleHeader: () => null,
});

// ─── Provider ────────────────────────────────────────────────────────────────

function computeIsDesktop(): boolean {
  return typeof window !== 'undefined' && window.innerWidth >= DT.breakpoint.desktop;
}

function computeMaxExpanded(): number {
  return typeof window !== 'undefined' && window.innerWidth >= ULTRAWIDE_MIN_WIDTH
    ? MAX_EXPANDED_ULTRAWIDE
    : MAX_EXPANDED_DESKTOP;
}

/**
 * Shell-level presentation coordinator for the desktop DM dock. Mount ONCE
 * inside AppShell (below FriendProvider, inside the router). Route changes
 * re-render this provider but never remount it, so open windows, minimized
 * tabs, drafts and ordering all survive navigation. Language switching only
 * relabels consumers — nothing here reads or resets on locale.
 */
export function ChatDockProvider({ children }: { children: React.ReactNode }) {
  const { identityKey } = useAuth();
  const { setDmToastsSuppressed } = useFriends();
  const suppressed = useDmPresentationSuppressed();
  const [state, dispatch] = useReducer(dockReducer, initialDockState);

  const [isDesktopDock, setIsDesktopDock] = useState(computeIsDesktop);
  const [maxExpanded, setMaxExpanded] = useState(computeMaxExpanded);
  const [documentVisible, setDocumentVisible] = useState(
    typeof document === 'undefined' ? true : document.visibilityState === 'visible',
  );

  // Drafts are ref-backed: keystrokes must not re-render the shell. They are
  // presentation state (unsent composer content), destroyed with the window
  // (close) and with the identity session (logout / account switch).
  const draftsRef = useRef(new Map<number, string>());

  const moduleHeaderRef = useRef<HTMLElement | null>(null);

  // ── Viewport tracking (dock breakpoint + ultrawide third window) ──────────
  useEffect(() => {
    const onResize = () => {
      setIsDesktopDock(computeIsDesktop());
      setMaxExpanded(computeMaxExpanded());
    };
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Shrinking the limit (e.g. ultrawide → standard) auto-minimizes the
  // least-recently-focused overflow. Only enforced while the dock is active —
  // below the breakpoint the dock UI is not rendered and state is preserved.
  useEffect(() => {
    if (isDesktopDock) dispatch({ type: 'ENFORCE_LIMIT', maxExpanded });
  }, [isDesktopDock, maxExpanded]);

  // ── Document visibility (read-eligibility input) ──────────────────────────
  useEffect(() => {
    const onVisibility = () => setDocumentVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // ── Route suppression → DM toast gate (presentation decides, the data
  //    layer only obeys the flag; caches and unread keep updating there). ────
  useEffect(() => {
    // Optional call: long-standing tests mock useFriends() partially; the
    // real provider always supplies the setter.
    setDmToastsSuppressed?.(suppressed);
    return () => setDmToastsSuppressed?.(false);
  }, [suppressed, setDmToastsSuppressed]);

  // ── Identity session boundary: logout and account switch destroy ALL dock
  //    state and drafts. Keyed on identityKey (same seam as FriendContext),
  //    with cleanup ALSO firing — so A→B→A starts from a clean dock every
  //    time and stale A-session state can never reappear. ────────────────────
  const firstIdentityRunRef = useRef(true);
  useEffect(() => {
    if (!firstIdentityRunRef.current) {
      dispatch({ type: 'RESET' });
      draftsRef.current = new Map();
    }
    firstIdentityRunRef.current = false;
    return () => {
      dispatch({ type: 'RESET' });
      draftsRef.current = new Map();
    };
  }, [identityKey]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const maxExpandedRef = useRef(maxExpanded);
  useEffect(() => { maxExpandedRef.current = maxExpanded; }, [maxExpanded]);

  const openConversation = useCallback((friendId: number) => {
    dispatch({ type: 'OPEN', id: friendId, maxExpanded: maxExpandedRef.current });
  }, []);
  const focusConversation = useCallback((friendId: number) => {
    dispatch({ type: 'FOCUS', id: friendId });
  }, []);
  const minimizeConversation = useCallback((friendId: number) => {
    dispatch({ type: 'MINIMIZE', id: friendId });
  }, []);
  const restoreConversation = useCallback((friendId: number) => {
    dispatch({ type: 'RESTORE', id: friendId, maxExpanded: maxExpandedRef.current });
  }, []);
  const closeConversation = useCallback((friendId: number) => {
    draftsRef.current.delete(friendId);  // a deliberately closed unsent draft is discarded
    dispatch({ type: 'CLOSE', id: friendId });
  }, []);
  const setModuleExpanded = useCallback((expanded: boolean) => {
    dispatch({ type: 'SET_MODULE_EXPANDED', expanded });
  }, []);

  const getDraft = useCallback((friendId: number) => draftsRef.current.get(friendId) ?? '', []);
  const setDraft = useCallback((friendId: number, html: string) => {
    draftsRef.current.set(friendId, html);
  }, []);

  const registerModuleHeader = useCallback((el: HTMLElement | null) => {
    moduleHeaderRef.current = el;
  }, []);
  const getModuleHeader = useCallback(() => moduleHeaderRef.current, []);

  const focusedId = state.focusOrder.length > 0 ? state.focusOrder[state.focusOrder.length - 1] : null;

  return (
    <ChatDockContext.Provider
      value={{
        openIds:          state.open,
        expandedIds:      state.focusOrder,
        minimizedIds:     state.minimizeOrder,
        focusedId,
        moduleExpanded:   state.moduleExpanded,
        lastEvent:        state.lastEvent,
        isDesktopDock,
        maxExpanded,
        suppressed,
        documentVisible,
        openConversation,
        focusConversation,
        minimizeConversation,
        restoreConversation,
        closeConversation,
        setModuleExpanded,
        getDraft,
        setDraft,
        registerModuleHeader,
        getModuleHeader,
      }}
    >
      {children}
    </ChatDockContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useChatDock(): ChatDockValue {
  return useContext(ChatDockContext);
}
